import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DesignPartnerGraphic } from '../components/DesignPartnerGraphic';
import { MarketingFooter, MarketingHeader } from '../components/MarketingChrome';
import { RequestAccessModal } from '../components/RequestAccessModal';

const conversationStarters = [
  'What are the most common maintenance issues?',
  'What patterns appear in the maintenance history?',
  'What does the available evidence say I should pay attention to?',
  'How complete is the available maintenance information?',
];

const intelligenceStages = [
  {
    number: '01',
    title: 'Learn.',
    description: 'Ingest and organize everything that matters about your machine.',
    items: ['Documents', 'Maintenance Logs', 'Inspection Reports', 'Work Orders', 'Technician Notes', 'OEM Manuals'],
    icon: 'bi-file-earmark-text',
  },
  {
    number: '02',
    title: 'Understand.',
    description: 'Extract facts, capture context, and connect how things relate.',
    items: ['Facts', 'Components', 'Procedures', 'Relationships', 'Conflicts', 'Context'],
    icon: 'bi-diagram-3',
  },
  {
    number: '03',
    title: 'Question.',
    description: 'Ask evidence-supported questions and get clear, sourced answers.',
    items: ['Natural Language', 'Evidence Links', 'Source Citations', 'Evidence Strength'],
    icon: 'bi-chat-left-text',
  },
  {
    number: '04',
    title: 'Investigate.',
    description: 'Follow leads, document findings, and build toward resolution.',
    items: ['Open Questions', 'Findings', 'Evidence', 'Root-Cause Analysis'],
    icon: 'bi-search',
  },
  {
    number: '05',
    title: 'Remember.',
    description: "Preserve what's learned and why decisions were made.",
    items: ['Version History', 'Lessons Learned', 'Decisions & Outcomes', 'Historical Context'],
    icon: 'bi-layers',
  },
];

const intelligenceMetrics = [
  { value: '135', label: 'Facts', icon: 'bi-journal-text' },
  { value: '51', label: 'Knowledge Chunks', icon: 'bi-share' },
  { value: '1', label: 'Conflict', icon: 'bi-exclamation-circle' },
  { value: '2', label: 'Open Investigations', icon: 'bi-search' },
  { value: 'Version 42', label: 'Intelligence Layer', icon: 'bi-layers' },
];

function ProductConversationPreview() {
  return (
    <div className="r42-marketing-product-frame" aria-label="Route42 conversation workspace preview">
      <div className="r42-marketing-product-shell">
        <aside className="r42-marketing-product-sidebar">
          <span className="r42-marketing-brand r42-marketing-brand--compact"><img src="/brand/route42-lockup-white-text.png" alt="Route42" width={1730} height={364} /></span>
          <div className="r42-marketing-machine-context">
            <span>Current machine</span>
            <strong>NHVf Pump 2</strong>
          </div>
          <button className="r42-marketing-new-conversation" type="button" tabIndex={-1}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            New conversation
          </button>
          <div className="r42-marketing-product-nav" aria-hidden="true">
            <span>Workspace</span>
            <b className="active"><i className="bi bi-chat-left-text" /> Conversations</b>
            <b><i className="bi bi-search" /> Investigations</b>
            <b><i className="bi bi-file-earmark-text" /> Documents</b>
            <b><i className="bi bi-cpu" /> Machine Memory</b>
            <b><i className="bi bi-clock-history" /> Activity</b>
          </div>
          <div className="r42-marketing-product-user" aria-hidden="true">EG</div>
        </aside>

        <section className="r42-marketing-conversation-preview">
          <div className="r42-marketing-conversation-copy">
            <span>Conversation workspace</span>
            <h2>What would you like to know about this machine?</h2>
            <p>25 documents are ready for grounded questions.</p>
          </div>

          <div className="r42-marketing-starters" aria-hidden="true">
            {conversationStarters.map((starter, index) => (
              <div key={starter} className="r42-marketing-starter">
                <i className={`bi ${index % 2 === 0 ? 'bi-file-earmark-text' : 'bi-chat-left-text'}`} />
                <span>{starter}</span>
              </div>
            ))}
          </div>

          <div className="r42-marketing-view-documents" aria-hidden="true">
            <i className="bi bi-file-earmark-text" />
            <span>View documents</span>
          </div>

          <div className="r42-marketing-composer" aria-hidden="true">
            <span>Ask Route42 about NHVf Pump 2...</span>
            <i className="bi bi-arrow-up" />
          </div>
        </section>
      </div>
    </div>
  );
}

export function LandingPage() {
  const location = useLocation();
  const stagesRef = useRef<HTMLDivElement>(null);
  const [requestOpen, setRequestOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Route42 | Machine Intelligence';
    return () => { document.title = previousTitle; };
  }, []);

  // Product navigation is handled by React Router, avoiding a white-page reload.
  // Scroll only after the landing page mounts when arriving from another page.
  useEffect(() => {
    if (location.hash !== '#intelligence-layer') return;
    document.getElementById('intelligence-layer')?.scrollIntoView();
  }, [location.hash]);

  function scrollStages(direction: -1 | 1) {
    const rail = stagesRef.current;
    if (!rail) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: direction * Math.max(280, rail.clientWidth * 0.72), behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  return (
    <main className="r42-marketing-page">
      <MarketingHeader onRequestAccess={() => setRequestOpen(true)} />

      <section className="r42-marketing-hero" id="top">
        <div className="r42-marketing-hero-grid">
          <div className="r42-marketing-hero-copy">
            <span className="r42-marketing-eyebrow">Machine Intelligence</span>
            <h1>Understand the machine. Not just the data.</h1>
            <p>
              Route42 creates a persistent intelligence layer around industrial equipment—learning from documents,
              history, observations, and operational knowledge so your people can question, investigate, and act on
              what the machine is telling them.
            </p>
            <div className="r42-marketing-actions">
              <button className="r42-marketing-primary" type="button" onClick={() => setRequestOpen(true)}>Request access <i className="bi bi-arrow-right" aria-hidden="true" /></button>
            </div>
          </div>
          <ProductConversationPreview />
        </div>
        <div className="r42-marketing-intelligence-line r42-marketing-intelligence-line--hero" aria-hidden="true">
          <span />
        </div>
      </section>

      <section className="r42-marketing-layer" id="intelligence-layer">
        <div className="r42-marketing-section-head">
          <span className="r42-marketing-eyebrow">The Machine Intelligence Layer</span>
          <h2>Everything known about the machine.<br />Finally connected.</h2>
          <p>Route42 continuously turns fragmented machine information into persistent, explorable intelligence.</p>
        </div>

        <div className="r42-marketing-stage-wrap">
          <button className="r42-marketing-stage-arrow r42-marketing-stage-arrow--left" type="button" aria-label="Previous intelligence stages" onClick={() => scrollStages(-1)}>
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
          <div className="r42-marketing-stages" ref={stagesRef}>
            {intelligenceStages.map((stage) => (
              <article className="r42-marketing-stage" key={stage.number}>
                <span className="r42-marketing-stage-number">{stage.number}</span>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
                <ul>
                  {stage.items.map((item) => (
                    <li key={item}><i className={`bi ${stage.icon}`} aria-hidden="true" /><span>{item}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <button className="r42-marketing-stage-arrow r42-marketing-stage-arrow--right" type="button" aria-label="Next intelligence stages" onClick={() => scrollStages(1)}>
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        </div>

        <div className="r42-marketing-metrics" aria-label="Example machine intelligence state">
          {intelligenceMetrics.map((metric) => (
            <div className="r42-marketing-metric" key={metric.label}>
              <div><strong>{metric.value}</strong><span>{metric.label}</span></div>
              <i className={`bi ${metric.icon}`} aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>

      <section className="r42-marketing-dp-teaser" aria-labelledby="design-partner-teaser-title">
        <div className="r42-marketing-dp-teaser-inner">
          <div className="r42-marketing-dp-teaser-copy">
            <span className="r42-marketing-eyebrow">Design Partner Program</span>
            <h2 id="design-partner-teaser-title">Help shape Machine Intelligence in the real world.</h2>
            <p>
              We’re selecting our first 10 manufacturing Design Partners—one machine each—to put Route42 into real
              operating environments, learn from real use, and help shape what comes next.
            </p>
            <strong>10 manufacturers. 10 machines. Real-world collaboration.</strong>
            <div className="r42-marketing-actions">
              <Link className="r42-marketing-primary" to="/design-partners">Explore the Design Partner Program <i className="bi bi-arrow-right" aria-hidden="true" /></Link>
            </div>
          </div>
          <DesignPartnerGraphic clean />
        </div>
      </section>

      <section className="r42-marketing-close" id="request-access">
        <div className="r42-marketing-close-grid" id="route42">
          <div className="r42-marketing-close-art" aria-hidden="true">
            <div className="r42-marketing-intelligence-line r42-marketing-intelligence-line--close"><span /></div>
          </div>
          <div className="r42-marketing-close-copy">
            <span className="r42-marketing-eyebrow">Route42 Machine Intelligence</span>
            <h2>The people responsible for the machine should never have to reconstruct its story from scratch.</h2>
            <p>
              Route42 gives manufacturers a shared, persistent understanding of what is known, what is changing,
              what conflicts, and what still needs to be resolved.
            </p>
            <div className="r42-marketing-actions">
              <button className="r42-marketing-primary" type="button" onClick={() => setRequestOpen(true)}>Join early access <i className="bi bi-arrow-right" aria-hidden="true" /></button>
            </div>
            <p className="r42-marketing-access-note">Early access is currently reviewed individually.</p>
          </div>
        </div>
      </section>

      <MarketingFooter />
      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </main>
  );
}
